import React, { useEffect, useState } from 'react';
import { Package } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { unctadApi } from '../../../services/api';
import type { ExportProductData } from '../../../services/api/unctadApi';

interface ExportProductsPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

// Product category colors
const PRODUCT_COLORS: Record<string, string> = {
  'Gold': 'bg-amber-500',
  'Natural Gas': 'bg-blue-500',
  'Cotton & Textiles': 'bg-emerald-500',
  'Copper & Products': 'bg-orange-500',
  'Food Products': 'bg-green-500',
  'Chemicals': 'bg-purple-500',
  'Vehicles & Parts': 'bg-cyan-500',
  'Machinery': 'bg-indigo-500',
};

export const ExportProductsPanel: React.FC<ExportProductsPanelProps> = ({
  isMinimized,
  onMinimize,
}) => {
  const [data, setData] = useState<ExportProductData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const response = await unctadApi.getExportProducts('UZB');
      if (response.data) {
        setData(response.data);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const exportDataArray = data.map(d => ({
    product: d.productCategory,
    value: d.value,
    share: d.share,
    year: d.year,
    source: d.source,
  }));

  // Calculate totals
  const totalValue = data.reduce((sum, d) => sum + d.value, 0);
  const topProductsShare = data.slice(0, 3).reduce((sum, d) => sum + d.share, 0);

  return (
    <Panel
      id="export-products"
      title="Export Products"
      subtitle="Composition"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      data={exportDataArray}
    >
      <div className="h-full flex flex-col">
        {/* Source Badge */}
        <div className="flex items-center gap-2 mb-2 px-1">
          <DataSourceBadge source={DATA_SOURCES.UNCTAD} showLabel size="xs" />
          <span className="text-xs text-gray-500">2023</span>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-xs">
            Loading export data...
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            {/* Composition Bar */}
            <div className="mb-3">
              <div className="text-xs text-gray-500 mb-1">Export Composition</div>
              <div className="h-4 bg-terminal-border rounded overflow-hidden flex">
                {data.map((product, idx) => (
                  <div
                    key={product.productCategory}
                    className={`${PRODUCT_COLORS[product.productCategory] || 'bg-gray-600'} transition-all`}
                    style={{ width: `${product.share}%` }}
                    title={`${product.productCategory}: ${product.share.toFixed(1)}%`}
                  />
                ))}
              </div>
            </div>

            {/* Products List */}
            <div className="space-y-1.5">
              {data.map((product, idx) => (
                <div
                  key={product.productCategory}
                  className="flex items-center gap-2 p-1.5 bg-terminal-border/20 rounded hover:bg-terminal-border/40 transition-colors"
                >
                  <div
                    className={`w-2 h-2 rounded-full ${PRODUCT_COLORS[product.productCategory] || 'bg-gray-600'}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-300 truncate">
                        {product.productCategory}
                      </span>
                      <span className="text-xs font-mono text-white">
                        ${(product.value / 1000).toFixed(1)}B
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="flex-1 h-1 bg-terminal-border rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${PRODUCT_COLORS[product.productCategory] || 'bg-gray-600'}`}
                          style={{ width: `${product.share * 3}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-8 text-right">
                        {product.share.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Diversification Summary */}
            <div className="mt-3 p-2 bg-terminal-border/30 rounded">
              <div className="flex items-center gap-1 mb-2">
                <Package size={12} className="text-data-amber" />
                <span className="text-xs text-gray-400">Export Structure</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div>
                  <div className="text-sm font-mono text-data-amber">
                    {topProductsShare.toFixed(0)}%
                  </div>
                  <div className="text-xs text-gray-500">Top 3 Products</div>
                </div>
                <div>
                  <div className="text-sm font-mono text-white">
                    {data.length}
                  </div>
                  <div className="text-xs text-gray-500">Categories</div>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500 text-center">
                {topProductsShare > 50 ? (
                  <span className="text-data-amber">High concentration - commodity dependent</span>
                ) : (
                  <span className="text-data-green">Well diversified export base</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-2 pt-1 border-t border-terminal-border/50 text-xs text-gray-600">
          Source: UNCTAD / ITC Trade Map
        </div>
      </div>
    </Panel>
  );
};

export default ExportProductsPanel;
